import { DomainError } from '../errors.js';

/**
 * Aylık iaşe raporu ve victualling rate (ADR 0123; ADR 0099 §13'ün sonraki aşaması). Saf hesap:
 * girdi geminin kumanya stok kalemleri, hareketleri (değiştirilemez `stock_movements`), mürettebat
 * atamaları ve şirketin bütçe oranları; "bugün" gemi yerel günüdür (CLAUDE.md "Zaman").
 *
 * - Dönem: ay (`YYYY-MM`); içinde bulunulan ay bugüne kadar, gelecek ay yok (`FUTURE_DATE`).
 * - Kişi-gün (POB): her gün geminin atamalı kişileri bir kez sayılır (`user_vessel_assignments`,
 *   bordronun gün hesabıyla aynı kaynak; ADR 0061).
 * - Değer: hareketin kendi fiyatı (giriş: GRN ya da elle giriş), yoksa lotunun giriş fiyatı; ikisi
 *   de yoksa değer bilinmez (uydurulmaz, kalem "fiyatsız" işaretlenir). Para birimleri toplanmaz:
 *   her tutar para birimi başına (kur yok; ADR 0047 §5, kasa emsali ADR 0093).
 * - Akış: açılış + giriş − tüketim − zayi/imha ± sayım farkı = kapanış (miktar ve değer). GRN
 *   iptalinin ters hareketi girişten düşer.
 * - Victualling rate = tüketim değeri / kişi-gün; yalnız mürettebat iaşesi (`Crew Provisions` ve
 *   maliyet merkezi boş kalemler). Otel/refah, tıbbi sarf ve misafir kumanyası ayrı gösterilir.
 * - Bütçe: şirketin para birimi başına günlük kişi başı oranı; yalnız aynı birimde karşılaştırılır.
 */

const QTY_PRECISION = 1e6;
const roundQty = (value: number) => Math.round(value * QTY_PRECISION) / QTY_PRECISION;
/** Tutar ve oran iki ondalık (gösterim); toplamlar yuvarlanmadan hesaplanır. */
const round2 = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100 || 0;

/** Victualling rate'e giren maliyet merkezi; boş merkez de girer (eski ve şirketin kendi kalemleri). */
export const VICTUALLING_RATE_COST_CENTERS = ['Crew Provisions'] as const;
/** Maliyet merkezlerinin rapordaki sırası (kütüphanenin sırası; boş merkez sonda). */
const VICTUALLING_COST_CENTER_ORDER = [
  'Crew Provisions',
  'Hotel & Welfare',
  'Medical',
  'Guest Provisions',
  null,
] as const;
const GRN_LINE_SOURCE = 'goods_receipt_line';
const INBOUND_KINDS = new Set(['receipt', 'in']);

export interface VictuallingItem {
  stockItemId: string;
  itemCode: string | null;
  name: string;
  unit: string;
  categoryCode: string | null;
  mainCategory: string;
  costCenter: string | null;
}

export interface VictuallingMovement {
  stockItemId: string;
  lotId: string | null;
  kind: string;
  /** İşaretli miktar: giriş artı, çıkış eksi. */
  quantity: number;
  /** Gemi yerel günü (`YYYY-MM-DD`). */
  localDate: string;
  sourceType: string | null;
  unitPrice: number | null;
  currency: string | null;
}

export interface PobAssignment {
  userId: string;
  from: string;
  to: string | null;
}

export interface VictuallingBudget {
  currency: string;
  /** Günlük kişi başı bütçe. */
  dailyRate: number;
}

export interface VictuallingInput {
  month: string;
  today: string;
  items: readonly VictuallingItem[];
  movements: readonly VictuallingMovement[];
  assignments: readonly PobAssignment[];
  budgets: readonly VictuallingBudget[];
}

/** Para birimi başına tutarlar (birime göre sıralı; sıfırlar yok). */
export type Money = { currency: string; amount: number }[];

export interface FlowQuantities {
  opening: number;
  receipts: number;
  consumption: number;
  disposal: number;
  /** Sayım farkı ve öteki düzeltmeler (işaretli). */
  adjustments: number;
  closing: number;
}

export interface FlowValues {
  opening: Money;
  receipts: Money;
  consumption: Money;
  disposal: Money;
  adjustments: Money;
  closing: Money;
}

export interface VictuallingItemRow extends VictuallingItem {
  quantity: FlowQuantities;
  value: FlowValues;
  /** Dönem sonuna kadar fiyatı bilinmeyen hareketi var. */
  unpriced: boolean;
  inRateBasis: boolean;
}

export interface VictuallingCategoryRow {
  categoryCode: string | null;
  mainCategory: string;
  items: number;
  unpricedItems: number;
  value: FlowValues;
}

export interface VictuallingCostCenterRow {
  costCenter: string | null;
  inRateBasis: boolean;
  consumption: Money;
  disposal: Money;
}

export interface VictuallingRate {
  currency: string;
  consumption: number;
  /** Kişi-gün yoksa `null`. */
  rate: number | null;
  budget: {
    dailyRate: number;
    amount: number;
    variance: number;
    /** Bütçe tutarı sıfırsa `null`. */
    variancePct: number | null;
  } | null;
}

export interface VictuallingReport {
  month: string;
  periodStart: string;
  periodEnd: string;
  days: number;
  /** Ay bitti mi (son günü bugünden önce). */
  complete: boolean;
  personDays: number;
  averagePob: number;
  dailyPob: { day: string; pob: number }[];
  items: VictuallingItemRow[];
  categories: VictuallingCategoryRow[];
  costCenters: VictuallingCostCenterRow[];
  totals: FlowValues;
  rates: VictuallingRate[];
  /** Raporda geçen para birimleri. */
  currencies: string[];
  unpricedItems: number;
}

/** Kategori kodu sırası: kodlular koda göre (sıralı karşılaştırma), kodsuzlar sonda. */
function codeOrder(a: string | null, b: string | null): number {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a < b ? -1 : 1;
}

const MONTH = /^(\d{4})-(0[1-9]|1[0-2])$/;
const DAY_MS = 86_400_000;

function dayNumber(day: string): number {
  return Date.parse(`${day}T00:00:00Z`) / DAY_MS;
}

function dayOf(n: number): string {
  return new Date(n * DAY_MS).toISOString().slice(0, 10);
}

/** Raporun dönemi; ay biçimi bozuksa `INVALID_MONTH`, gelecek aysa `FUTURE_DATE`. */
export function victuallingPeriod(
  month: string,
  today: string,
): { start: string; end: string; days: number; complete: boolean } {
  const match = MONTH.exec(month);
  if (!match) throw new DomainError('INVALID_MONTH', month);
  if (month > today.slice(0, 7)) throw new DomainError('FUTURE_DATE', month);
  const year = Number(match[1]);
  const index = Number(match[2]);
  const start = `${month}-01`;
  const monthEnd = dayOf(Date.UTC(year, index, 0) / DAY_MS);
  const end = monthEnd < today ? monthEnd : today;
  return { start, end, days: dayNumber(end) - dayNumber(start) + 1, complete: monthEnd < today };
}

/** Dönemin kişi-günü ve günlük POB: her gün atamalı kişiler bir kez (ters atama yok sayılır). */
export function personDays(
  assignments: readonly PobAssignment[],
  start: string,
  end: string,
): { personDays: number; daily: { day: string; pob: number }[] } {
  const first = dayNumber(start);
  const last = dayNumber(end);
  const people = Array.from({ length: Math.max(0, last - first + 1) }, () => new Set<string>());
  for (const a of assignments) {
    if (a.to !== null && a.to < a.from) continue;
    const from = Math.max(first, dayNumber(a.from));
    const to = Math.min(last, a.to === null ? last : dayNumber(a.to));
    for (let d = from; d <= to; d += 1) people[d - first]?.add(a.userId);
  }
  const daily = people.map((set, i) => ({ day: dayOf(first + i), pob: set.size }));
  return { personDays: daily.reduce((sum, d) => sum + d.pob, 0), daily };
}

type FlowKey = keyof FlowQuantities;
const FLOW_KEYS: readonly FlowKey[] = [
  'opening',
  'receipts',
  'consumption',
  'disposal',
  'adjustments',
  'closing',
];

/** Hareketin dönem içindeki yeri; tüketim ve zayi artı yazılır. */
function flowOf(m: VictuallingMovement): { key: FlowKey; sign: 1 | -1 } {
  if (INBOUND_KINDS.has(m.kind)) return { key: 'receipts', sign: 1 };
  if (m.kind === 'adjust' && m.sourceType === GRN_LINE_SOURCE) return { key: 'receipts', sign: 1 };
  if (m.kind === 'out') return { key: 'consumption', sign: -1 };
  if (m.kind === 'dispose') return { key: 'disposal', sign: -1 };
  return { key: 'adjustments', sign: 1 };
}

class Purse {
  readonly amounts = new Map<string, number>();
  add(currency: string, amount: number): void {
    this.amounts.set(currency, (this.amounts.get(currency) ?? 0) + amount);
  }
  merge(other: Purse, sign = 1): void {
    for (const [currency, amount] of other.amounts) this.add(currency, amount * sign);
  }
  money(): Money {
    return [...this.amounts.entries()]
      .map(([currency, amount]) => ({ currency, amount: round2(amount) }))
      .filter((m) => m.amount !== 0)
      .sort((a, b) => a.currency.localeCompare(b.currency));
  }
}

type Purses = Record<FlowKey, Purse>;
const purses = (): Purses =>
  Object.fromEntries(FLOW_KEYS.map((key) => [key, new Purse()])) as Purses;
const moneyOf = (p: Purses): FlowValues =>
  Object.fromEntries(FLOW_KEYS.map((key) => [key, p[key].money()])) as unknown as FlowValues;

const inRateBasis = (costCenter: string | null) =>
  costCenter === null || (VICTUALLING_RATE_COST_CENTERS as readonly string[]).includes(costCenter);

/** Aylık iaşe raporu (ADR 0123). */
export function victuallingReport(input: VictuallingInput): VictuallingReport {
  const period = victuallingPeriod(input.month, input.today);
  const pob = personDays(input.assignments, period.start, period.end);
  const moves = input.movements.filter((m) => m.localDate <= period.end);

  // Lotun fiyatı: lotu açan giriş hareketinin fiyatı (GRN satırı ya da elle giriş)
  const lotPrices = new Map<string, { unitPrice: number; currency: string }>();
  for (const m of [...moves].sort((a, b) => a.localDate.localeCompare(b.localDate))) {
    if (
      m.lotId !== null &&
      !lotPrices.has(m.lotId) &&
      INBOUND_KINDS.has(m.kind) &&
      m.quantity > 0 &&
      m.unitPrice !== null &&
      m.currency !== null
    ) {
      lotPrices.set(m.lotId, { unitPrice: m.unitPrice, currency: m.currency });
    }
  }
  const priceOf = (m: VictuallingMovement) =>
    m.unitPrice !== null && m.currency !== null
      ? { unitPrice: m.unitPrice, currency: m.currency }
      : m.lotId !== null
        ? (lotPrices.get(m.lotId) ?? null)
        : null;

  const byItem = new Map<string, VictuallingMovement[]>();
  for (const m of moves) byItem.set(m.stockItemId, [...(byItem.get(m.stockItemId) ?? []), m]);

  const rows: (VictuallingItemRow & { purses: Purses })[] = [];
  for (const item of input.items) {
    const list = byItem.get(item.stockItemId) ?? [];
    const quantity: FlowQuantities = {
      opening: 0,
      receipts: 0,
      consumption: 0,
      disposal: 0,
      adjustments: 0,
      closing: 0,
    };
    const p = purses();
    let unpriced = false;
    let inPeriod = false;
    for (const m of list) {
      const price = priceOf(m);
      if (!price) unpriced = true;
      const value = price ? m.quantity * price.unitPrice : 0;
      quantity.closing += m.quantity;
      if (price) p.closing.add(price.currency, value);
      if (m.localDate < period.start) {
        quantity.opening += m.quantity;
        if (price) p.opening.add(price.currency, value);
        continue;
      }
      inPeriod = true;
      const { key, sign } = flowOf(m);
      quantity[key] += sign * m.quantity;
      if (price) p[key].add(price.currency, sign * value);
    }
    for (const key of FLOW_KEYS) quantity[key] = roundQty(quantity[key]);
    if (!inPeriod && quantity.opening === 0) continue;
    rows.push({
      ...item,
      quantity,
      value: moneyOf(p),
      unpriced,
      inRateBasis: inRateBasis(item.costCenter),
      purses: p,
    });
  }
  rows.sort(
    (a, b) =>
      codeOrder(a.categoryCode, b.categoryCode) ||
      a.mainCategory.localeCompare(b.mainCategory, 'en') ||
      a.name.localeCompare(b.name, 'en'),
  );

  const categories = new Map<
    string,
    {
      categoryCode: string | null;
      mainCategory: string;
      items: number;
      unpricedItems: number;
      purses: Purses;
    }
  >();
  const centers = new Map<
    string,
    { costCenter: string | null; consumption: Purse; disposal: Purse }
  >();
  const totals = purses();
  const rateBasis = new Purse();
  for (const row of rows) {
    const key = `${row.categoryCode ?? '~'}|${row.mainCategory}`;
    const category = categories.get(key) ?? {
      categoryCode: row.categoryCode,
      mainCategory: row.mainCategory,
      items: 0,
      unpricedItems: 0,
      purses: purses(),
    };
    category.items += 1;
    if (row.unpriced) category.unpricedItems += 1;
    for (const flow of FLOW_KEYS) {
      category.purses[flow].merge(row.purses[flow]);
      totals[flow].merge(row.purses[flow]);
    }
    categories.set(key, category);
    const center = centers.get(row.costCenter ?? '') ?? {
      costCenter: row.costCenter,
      consumption: new Purse(),
      disposal: new Purse(),
    };
    center.consumption.merge(row.purses.consumption);
    center.disposal.merge(row.purses.disposal);
    centers.set(row.costCenter ?? '', center);
    if (row.inRateBasis) rateBasis.merge(row.purses.consumption);
  }

  const budgets = new Map(input.budgets.map((b) => [b.currency, b.dailyRate]));
  const rates: VictuallingRate[] = rateBasis
    .money()
    .filter((m) => m.amount > 0)
    .map(({ currency }) => {
      const consumption = rateBasis.amounts.get(currency) ?? 0;
      const dailyRate = budgets.get(currency);
      const amount = dailyRate === undefined ? 0 : dailyRate * pob.personDays;
      return {
        currency,
        consumption: round2(consumption),
        rate: pob.personDays > 0 ? round2(consumption / pob.personDays) : null,
        budget:
          dailyRate === undefined
            ? null
            : {
                dailyRate,
                amount: round2(amount),
                variance: round2(consumption - amount),
                variancePct: amount > 0 ? round2(((consumption - amount) / amount) * 100) : null,
              },
      };
    });

  const centerOrder = (c: string | null) => {
    const index = (VICTUALLING_COST_CENTER_ORDER as readonly (string | null)[]).indexOf(c);
    return index === -1 ? VICTUALLING_COST_CENTER_ORDER.length : index;
  };
  const currencies = new Set<string>();
  for (const flow of FLOW_KEYS) for (const m of totals[flow].money()) currencies.add(m.currency);

  return {
    month: input.month,
    periodStart: period.start,
    periodEnd: period.end,
    days: period.days,
    complete: period.complete,
    personDays: pob.personDays,
    averagePob: period.days > 0 ? round2(pob.personDays / period.days) : 0,
    dailyPob: pob.daily,
    items: rows.map(({ purses: _purses, ...row }) => row),
    categories: [...categories.values()]
      .sort(
        (a, b) =>
          codeOrder(a.categoryCode, b.categoryCode) ||
          a.mainCategory.localeCompare(b.mainCategory, 'en'),
      )
      .map(({ purses: p, ...category }) => ({ ...category, value: moneyOf(p) })),
    costCenters: [...centers.values()]
      .sort(
        (a, b) =>
          centerOrder(a.costCenter) - centerOrder(b.costCenter) ||
          (a.costCenter ?? '').localeCompare(b.costCenter ?? ''),
      )
      .map((c) => ({
        costCenter: c.costCenter,
        inRateBasis: inRateBasis(c.costCenter),
        consumption: c.consumption.money(),
        disposal: c.disposal.money(),
      })),
    totals: moneyOf(totals),
    rates,
    currencies: [...currencies].sort(),
    unpricedItems: rows.filter((r) => r.unpriced).length,
  };
}

export interface VictuallingFleetVessel {
  vesselId: string;
  name: string;
  personDays: number;
  averagePob: number;
  rates: VictuallingRate[];
  unpricedItems: number;
}

export interface VictuallingFleet {
  vessels: VictuallingFleetVessel[];
  /** Para birimi başına filo oranı: o birimde tüketimi olan gemilerin toplamı / kişi-günü. */
  totals: {
    currency: string;
    consumption: number;
    personDays: number;
    rate: number | null;
    vessels: number;
  }[];
}

/** Ofisin filo görünümü (ADR 0123): gemi satırları ve para birimi başına filo oranı. */
export function victuallingFleet(
  reports: readonly { vesselId: string; name: string; report: VictuallingReport }[],
): VictuallingFleet {
  const totals = new Map<string, { consumption: number; personDays: number; vessels: number }>();
  for (const { report } of reports) {
    for (const rate of report.rates) {
      const entry = totals.get(rate.currency) ?? { consumption: 0, personDays: 0, vessels: 0 };
      entry.consumption += rate.consumption;
      entry.personDays += report.personDays;
      entry.vessels += 1;
      totals.set(rate.currency, entry);
    }
  }
  return {
    vessels: reports
      .map(({ vesselId, name, report }) => ({
        vesselId,
        name,
        personDays: report.personDays,
        averagePob: report.averagePob,
        rates: report.rates,
        unpricedItems: report.unpricedItems,
      }))
      .sort((a, b) => a.name.localeCompare(b.name, 'en')),
    totals: [...totals.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([currency, t]) => ({
        currency,
        consumption: round2(t.consumption),
        personDays: t.personDays,
        rate: t.personDays > 0 ? round2(t.consumption / t.personDays) : null,
        vessels: t.vessels,
      })),
  };
}
